import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API, auth, money, fmtDate, PageHead, PrimaryBtn, FinanceEmpty, StatusBadge, PrintStyle, printArea, EXPENSE_CATS, REVENUE_CATS } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';

const PERIODS = [{ k: 'month', l: 'هذا الشهر' }, { k: 'quarter', l: 'آخر 3 أشهر' }, { k: 'year', l: 'هذه السنة' }, { k: 'all', l: 'الكل' }];
const VALID_PERIOD_KEYS = PERIODS.map((p) => p.k);

const inPeriod = (dateStr, period) => {
  if (period === 'all' || !dateStr) return true;
  const d = new Date(dateStr); const now = new Date();
  if (period === 'year') return d.getFullYear() === now.getFullYear();
  if (period === 'quarter') { const q = new Date(); q.setMonth(q.getMonth() - 3); return d >= q; }
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

export default function FinanceSummaryPage() {
  const [searchParams] = useSearchParams();
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialPeriod = VALID_PERIOD_KEYS.includes(searchParams.get('period')) ? searchParams.get('period') : 'year';
  const [period, setPeriod] = useState(initialPeriod);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [e, r, i, a] = await Promise.allSettled([
      axios.get(`${API}/expenses`, auth()),
      axios.get(`${API}/revenues`, auth()),
      axios.get(`${API}/club-invoices?limit=100`, auth()),
      axios.get(`${API}/financial/audit-log?limit=50`, auth()),
    ]);
    if (e.status === 'fulfilled') setExpenses(e.value.data || []);
    if (r.status === 'fulfilled') setRevenues(r.value.data || []);
    if (i.status === 'fulfilled') setInvoices(i.value.data?.items || []);
    if (a.status === 'fulfilled') setAudit(a.value.data || []);
    if (e.status === 'rejected' && r.status === 'rejected') setError(e.reason?.response?.status === 403 ? 'لا تملك صلاحية الاطلاع على البيانات المالية' : 'تعذر تحميل البيانات المالية');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const AUDIT_ACTION_LABELS = { create: 'إنشاء', update: 'تعديل', void: 'إلغاء', cancel: 'إلغاء فاتورة', record_payment: 'تسجيل دفعة' };
  const auditLabel = (a) => AUDIT_ACTION_LABELS[a] || (a?.startsWith('status_') ? `تغيير الحالة إلى ${a.replace('status_', '')}` : a);

  if (loading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-200/70" />)}</div>;
  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="finance-error">{error}</div>;

  const exp = expenses.filter((x) => x.status !== 'rejected' && x.status !== 'voided' && inPeriod(x.expense_date, period));
  const rev = revenues.filter((x) => x.status !== 'voided' && inPeriod(x.revenue_date, period));
  const totalExp = exp.reduce((s, x) => s + x.amount, 0);
  const totalRev = rev.reduce((s, x) => s + x.amount, 0);
  const receivables = invoices.filter((i) => ['unpaid', 'partial'].includes(i.status)).reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);

  // مقارنة شهرية (آخر 6 أشهر)
  const months = [];
  for (let k = 5; k >= 0; k--) {
    const d = new Date(); d.setMonth(d.getMonth() - k);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const label = new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { month: 'short' }).format(d);
    const er = expenses.filter((x) => x.status !== 'rejected' && x.status !== 'voided' && x.expense_date && `${new Date(x.expense_date).getFullYear()}-${new Date(x.expense_date).getMonth()}` === key).reduce((s, x) => s + x.amount, 0);
    const rr = revenues.filter((x) => x.status !== 'voided' && x.revenue_date && `${new Date(x.revenue_date).getFullYear()}-${new Date(x.revenue_date).getMonth()}` === key).reduce((s, x) => s + x.amount, 0);
    months.push({ label, exp: er, rev: rr });
  }
  const maxV = Math.max(1, ...months.flatMap((m) => [m.exp, m.rev]));

  const latest = [
    ...exp.map((x) => ({ id: x.id, kind: 'مصروف', title: x.title, amount: -x.amount, date: x.expense_date, cat: EXPENSE_CATS[x.category], status: x.status })),
    ...rev.map((x) => ({ id: x.id, kind: 'إيراد', title: x.title, amount: x.amount, date: x.revenue_date, cat: REVENUE_CATS[x.category] })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8);

  return (
    <div className="mx-auto max-w-7xl space-y-4 print-area" data-testid="finance-summary-page">
      <PrintStyle />
      <PageHead title="الملخص المالي" desc="نظرة شاملة على الوضع المالي للمنشأة"
        action={<div className="flex gap-2 print:hidden">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            {PERIODS.map((p) => <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`} className={`rounded-md px-3 py-1.5 text-xs ${period === p.k ? 'bg-[#16233b] text-white font-semibold' : 'text-slate-600'}`}>{p.l}</button>)}
          </div>
          <PrimaryBtn onClick={printArea} data-testid="summary-print-btn"><Icon name="document" className="w-4 h-4" /> طباعة</PrimaryBtn>
        </div>} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="finance-kpis">
        {[
          { t: 'إجمالي الإيرادات', v: totalRev, c: 'text-teal-700', id: 'kpi-revenues' },
          { t: 'إجمالي المصروفات', v: totalExp, c: 'text-red-600', id: 'kpi-expenses' },
          { t: 'صافي المركز المالي', v: totalRev - totalExp, c: totalRev - totalExp >= 0 ? 'text-slate-900' : 'text-red-600', id: 'kpi-net' },
          { t: 'المستحقات (فواتير غير محصلة)', v: receivables, c: 'text-amber-600', id: 'kpi-receivables' },
        ].map((k) => (
          <div key={k.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={k.id}>
            <div className="text-xs text-slate-500">{k.t}</div>
            <div className={`mt-1 text-xl font-bold ${k.c}`} dir="rtl">{money(k.v)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="monthly-comparison">
          <h3 className="mb-4 text-sm font-bold text-slate-800">مقارنة شهرية (آخر 6 أشهر)</h3>
          <div className="flex items-end justify-between gap-2" style={{ height: 160 }}>
            {months.map((m, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-end justify-center gap-1" style={{ height: 130 }}>
                  <div className="w-3 rounded-t bg-teal-500" style={{ height: `${(m.rev / maxV) * 100}%` }} title={`إيرادات: ${money(m.rev)}`} />
                  <div className="w-3 rounded-t bg-red-400" style={{ height: `${(m.exp / maxV) * 100}%` }} title={`مصروفات: ${money(m.exp)}`} />
                </div>
                <span className="text-[10px] text-slate-500">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-teal-500" /> إيرادات</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400" /> مصروفات</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="latest-transactions">
          <h3 className="mb-3 text-sm font-bold text-slate-800">أحدث العمليات</h3>
          {latest.length === 0 ? <FinanceEmpty text="لا توجد عمليات مالية في هذه الفترة" /> : (
            <ul className="space-y-2">
              {latest.map((t) => (
                <li key={`${t.kind}-${t.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-800">{t.title}</div>
                    <div className="text-xs text-slate-400">{t.kind}{t.cat ? ` • ${t.cat}` : ''} • {fmtDate(t.date)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.status && <StatusBadge status={t.status} />}
                    <span className={`text-sm font-bold ${t.amount >= 0 ? 'text-teal-700' : 'text-red-600'}`} dir="rtl">{money(Math.abs(t.amount))}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 print:hidden" data-testid="financial-audit-panel">
        <button onClick={() => setShowAudit(!showAudit)} className="flex w-full items-center justify-between text-sm font-bold text-slate-800" data-testid="financial-audit-toggle">
          <span className="flex items-center gap-2"><Icon name="document" className="w-4 h-4" /> سجل التدقيق المالي ({audit.length})</span>
          <Icon name="chevronDown" className={`w-4 h-4 transition-transform ${showAudit ? 'rotate-180' : ''}`} />
        </button>
        {showAudit && (
          audit.length === 0 ? <FinanceEmpty text="لا توجد أحداث مسجّلة بعد" /> : (
            <ul className="mt-3 max-h-80 space-y-1.5 overflow-y-auto text-xs">
              {audit.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2 border-b border-slate-50 py-1.5" data-testid="audit-log-row">
                  <span className="text-slate-600">{a.entity_type} • {auditLabel(a.action)}{a.reason ? ` — ${a.reason}` : ''}</span>
                  <span className="text-slate-400">{a.performed_by_name} • {fmtDate(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}
